import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Ticket } from "lucide-react";

export default function QuizPlayPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/quizzes")
      .then(({ data }) => {
        const q = data.find((x) => x.id === id);
        if (!q) { toast.error("Quiz introuvable"); navigate("/jeux"); return; }
        setQuiz(q);
        setAnswers(new Array(q.questions.length).fill(-1));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [id, navigate]);

  if (!quiz) return <p className="text-plum-700/50 italic">Chargement...</p>;

  const current = quiz.questions[step];
  const isLast = step === quiz.questions.length - 1;
  const pct = Math.round(((step + 1) / quiz.questions.length) * 100);

  const select = (oi) => {
    const next = [...answers]; next[step] = oi; setAnswers(next);
  };

  const next = () => {
    if (answers[step] < 0) return toast.error("Choisissez une réponse");
    setStep(step + 1);
  };

  const submit = async () => {
    if (answers[step] < 0) return toast.error("Choisissez une réponse");
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${id}/attempt`, { answers });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto py-8 animate-reward-pop" data-testid="quiz-result">
        {result.won ? (
          <div className="space-y-10" data-testid="reward-reveal">
            <div className="phenix-eyebrow text-pink-500">Récompense débloquée</div>
            <h2 className="heading-display text-3xl text-plum-900">
              <em className="text-pink-500">Bravo.</em><br />
              Vous avez tout bon.
            </h2>
            <p className="text-base text-plum-700/70 max-w-lg leading-relaxed">
              {result.score}/{result.total} bonnes réponses. Vous remportez un bon de réduction.
            </p>
            <div className="border-y border-cream-200 py-10">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-2">
                  <Ticket size={48} strokeWidth={1} className="text-pink-500" />
                </div>
                <div className="lg:col-span-7">
                  <div className="phenix-eyebrow mb-2">Votre bon</div>
                  <div className="font-display text-4xl text-ink-900">{quiz.reward_label}</div>
                  {quiz.merchant_name && (
                    <div className="text-base text-plum-700/70 italic mt-2">chez {quiz.merchant_name}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-6">
              <Button onClick={() => navigate("/bons")} className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-8 group">
                Voir mes bons
                <ArrowUpRight size={14} className="ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
              <button onClick={() => navigate("/jeux")} className="btn-ghost-line text-sm">
                Autres quiz
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="phenix-eyebrow text-plum-700/50">Résultat</div>
            <h2 className="heading-display text-2xl lg:text-3xl text-plum-900">
              <em className="text-pink-500">Pas tout à fait…</em>
            </h2>
            <p className="text-base text-plum-700/70 max-w-lg leading-relaxed">
              {result.score}/{result.total} bonnes réponses. Il faut tout réussir pour décrocher le bon.
            </p>
            <Button onClick={() => navigate("/jeux")} className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-8">
              Retour aux quiz
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-10">
      <button onClick={() => navigate(-1)} className="btn-ghost-line text-xs">
        <ArrowLeft size={12} /> Retour
      </button>

      <header>
        <div className="phenix-eyebrow mb-3">Quiz</div>
        <h1 className="heading-display text-xl text-plum-900">{quiz.title}</h1>
      </header>

      <div className="flex items-center gap-6 border-y border-cream-200 py-5">
        <div className="phenix-eyebrow shrink-0">Question</div>
        <Progress value={pct} className="h-px flex-1 bg-ink-900/10" />
        <span className="font-mono text-xs text-plum-700 tabular-nums">
          {String(step + 1).padStart(2, "0")}/{String(quiz.questions.length).padStart(2, "0")}
        </span>
      </div>

      <div className="space-y-8 animate-fade-up" key={step}>
        <h3 className="editorial-title text-3xl lg:text-4xl text-ink-900 leading-tight">
          {current.question}
        </h3>
        <div className="divide-y divide-cream-200 border-y border-cream-200">
          {current.options.map((opt, oi) => (
            <button
              key={oi}
              type="button"
              onClick={() => select(oi)}
              data-testid={`quiz-option-${step}-${oi}`}
              className={`w-full text-left flex items-center gap-5 py-5 transition-colors px-2 -mx-2 ${
                answers[step] === oi
                  ? "bg-cream-50 text-ink-900"
                  : "hover:bg-cream-50 text-plum-700/70 hover:text-ink-900"
              }`}
            >
              <span className={`font-mono text-[11px] w-6 ${answers[step] === oi ? "text-pink-500" : "text-plum-700/50"}`}>
                {String.fromCharCode(65 + oi)}
              </span>
              <span className="font-display text-xl flex-1">{opt}</span>
              {answers[step] === oi && <span className="w-2 h-2 rounded-full bg-clay-500" />}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-cream-200 pt-8">
        {isLast ? (
          <Button onClick={submit} disabled={busy}
            className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-8 group"
            data-testid="quiz-submit-btn">
            Valider mes réponses
            <ArrowUpRight size={14} className="ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Button>
        ) : (
          <Button onClick={next}
            className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-8 group"
            data-testid="quiz-next-btn">
            Question suivante
            <ArrowUpRight size={14} className="ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
